import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css"; // Leaflet styles
import Spinner from "react-bootstrap/Spinner";
import ContentWrapper from "../Atomics/ContentWrapper/ContentWrapper";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import ThinFooter from "../Footer/ThinFooter";
import axiosInstance from "../../axiosInstance";

const SingleTrack = () => {
  const { id } = useParams(); // Get track ID from route params
  const history = useHistory();
  const [track, setTrack] = useState(null); // Store single track details
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [userRole, setUserRole] = useState(null); // ✅ Added userRole state
  const [map, setMap] = useState(null); // Leaflet map instance
  const [coordinates, setCoordinates] = useState(null); // Store fetched coordinates

  // Fetch single track details
  useEffect(() => {
    fetchSingleTrack();
    fetchUserRole();
  }, [id]);

   // Fetch user role from /users/currentUser
  const fetchUserRole = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get("/users/currentUser", {
                  headers: {
                        "Authorization": token,
                  },
            });

       if (response.data.success && response.data.data.user) {
                    setUserRole(response.data.data.user.role); // Set the user role
                }
    } catch (error) {
                console.error("Error fetching user role:", error);
            }
  };

  const fetchSingleTrack = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axiosInstance.get(`/track/${id}`, {
        headers: {
          Authorization: token,
        },
      });

      if (response.status === 200 && response.data.success) {
        const trackData = response.data.data;
        setTrack(trackData); // Store track data
        setError(null);

        const address = trackData.address;
        if (address) {
          fetchCoordinates(address); // Fetch coordinates for the address
        }
      } else {
        setError("Track not found.");
      }
    } catch (error) {
      console.error("Error fetching track:", error);
      setError("Failed to fetch track. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCoordinates = async (address) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`
      );
      const data = await response.json();
      if (data.length > 0) {
        const { lat, lon } = data[0];
        setCoordinates({ lat: parseFloat(lat), lon: parseFloat(lon) });
      } else {
        console.error("No coordinates found for address");
        setError("Could not find location for the address.");
      }
    } catch (error) {
      console.error("Error fetching coordinates:", error);
      setError("Failed to fetch coordinates for the address.");
    }
  };

  // Initialize map after coordinates are fetched
  useEffect(() => {
    if (coordinates && document.getElementById("map")) {
      if (map) {
        map.remove(); // Remove the existing map instance
      }

      const mapInstance = L.map("map", {
        center: [coordinates.lat, coordinates.lon],
        zoom: 15,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(mapInstance);

      setMap(mapInstance); // Set new map instance
    }
  }, [coordinates]);

  const handleBack = () => {
    history.push("/track");
  };

  const handleAddConfiguration = () => {
    history.push("/add-configurations");
  };

  const handleConfigurationClick = (configurationId) => {
    history.push(`/config/${configurationId}`);
  };

  if (loading) {
    return (
      <Container>
        <Row
          className="text-center w-100"
          style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <Spinner animation="border" />
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Row
          className="text-center w-100"
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <h4>{error}</h4>
        </Row>
      </Container>
    );
  }

  return (
    <ContentWrapper>
      <Container fluid>
        {/* Track Details */}
        <Row className="mb-3">
          <Col xs={12}>
            <h2>{track.trackName}</h2> {/* Track Title */}
          </Col>
        </Row>

        {/* Map */}
        <Row>
          <Col xs={12}>
            <div id="map" style={{ height: "700px", width: "100%" }}></div>
          </Col>
        </Row>

        {/* Configurations */}
        <Row id="mb-rw" className="mb-3 row mt-4">
          {track.configurations && track.configurations.length > 0 ? (
            track.configurations.map((config) => (
              <Col key={config.configurationId} md={4} className="mb-3 col-12">
                <div className="track-card">
                  <div
                    style={{
                      backgroundColor: "#ccc",
                      height: "200px",
                      marginBottom: "10px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleConfigurationClick(config.configurationId)}
                  >
                    <img
                      src={track.mapImage || "https://via.placeholder.com/150"}
                      alt={config.configurationName}
                      style={{
                        height: "200px",
                        marginBottom: "10px",
                        width: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                  <div className="track-info">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5>{config.configurationName}</h5>
                    </div>
                  </div>
                </div>
              </Col>
            ))
          ) : (
            <Col xs={12}>
              <p>No configurations found for this track.</p>
            </Col>
          )}
        </Row>

        {/* Buttons */}
        <Row className="mt-4">
          <Col xs={6} className="text-left">
           {userRole === "Admin" && (
            <button className="btn btn-success sv_btn" onClick={handleAddConfiguration}>
              + Add New Configurations
            </button>
            )}  
          </Col>
          <Col xs={6} className="text-right">
            <button className="btn btn-primary sv_btn" onClick={handleBack}>
              Back to Tracks
            </button>
          </Col>
        </Row>
      </Container>
      <ThinFooter />
    </ContentWrapper>
  );
};

export default SingleTrack;
